import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import heroVideo from "../assests/Prisuvi.mp4";

export default function ECommerceRetail() {
  const [videoLoaded, setVideoLoaded] = useState(false);

  return (
    <div className="bg-gray-900 text-gray-100 font-orbitron relative overflow-hidden">
      {/* Hero Section */}
      <section className="relative w-full h-[70vh] flex items-center justify-center text-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover brightness-75 transition-opacity duration-1000 ease-in-out ${
            videoLoaded ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Overlay for gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-700/70 via-cyan-600/50 to-purple-700/70" />

        {/* Hero Content */}
        <div className="relative z-10 px-6 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={videoLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-wider text-white drop-shadow-lg"
          >
            E-commerce & Retail AI Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={videoLoaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-gray-200"
          >
            Transform your retail operations with AI-powered insights, smarter inventory, and personalized shopping experiences.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <motion.section
        className="py-20 px-6 text-center max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold mb-6 text-cyan-400 tracking-wider">Why Data & AI Matter in Retail</h2>
        <p className="text-lg text-gray-300 leading-relaxed">
          AI-driven datasets help retailers predict demand, optimize inventory, and personalize the shopping experience at scale. Our curated datasets power smart AI models for real-world retail applications.
        </p>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left text-gray-300 text-base">
          <li>✔ Personalized product recommendations</li>
          <li>✔ Inventory & supply chain optimization</li>
          <li>✔ Customer behavior & sentiment analysis</li>
          <li>✔ Enhanced product search & categorization</li>
        </ul>
      </motion.section>

      {/* Services Section */}
      <motion.section
        className="py-20 px-6"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold text-center mb-12 text-cyan-400 tracking-wider">Our AI Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "📦 Product Data Collection",
              desc: "Images, videos, and text annotated for AI cataloging and search optimization."
            },
            {
              title: "🛒 Customer Behavior Analysis",
              desc: "Datasets to analyze browsing patterns, engagement, and purchase behavior."
            },
            {
              title: "💬 Sentiment & Review Analysis",
              desc: "Clean customer feedback data to train recommendation engines and NLP models."
            },
            {
              title: "📊 Inventory & Pricing Insights",
              desc: "Structured datasets for AI-driven inventory management and dynamic pricing."
            }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-gray-800/50 p-6 rounded-3xl backdrop-blur-md border border-gray-700 shadow-lg hover:shadow-2xl transition"
            >
              <h3 className="text-xl font-semibold mb-3 text-cyan-300">{item.title}</h3>
              <p className="text-gray-300 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Workflow Section */}
      <motion.section
        className="py-20 px-6 text-center"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold text-cyan-400 mb-12 tracking-wider">How We Work</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Define project goals & AI dataset requirements",
            "Collect product, customer, and transaction data",
            "Annotate & label datasets for AI accuracy",
            "QA & deliver ready-to-use AI datasets"
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="p-6 bg-gray-800/50 rounded-2xl backdrop-blur-md border border-gray-700 shadow-md hover:shadow-xl transition"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-300 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Applications Section */}
      <motion.section
        className="py-20 px-6 bg-gray-900"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold text-center mb-12 text-cyan-400 tracking-wider">Applications</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "🛍 Personalized Shopping",
              desc: "AI recommendations tailored to each customer."
            },
            {
              title: "📦 Inventory Optimization",
              desc: "Predictive analytics for smart stock management."
            },
            {
              title: "💰 Dynamic Pricing",
              desc: "Data-driven pricing strategies to maximize profit."
            },
            {
              title: "🤖 Customer Support AI",
              desc: "Chatbots & voice assistants powered by AI datasets."
            }
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className="bg-gray-800/50 p-6 rounded-3xl backdrop-blur-md border border-gray-700 shadow-md hover:shadow-xl transition"
            >
              <h3 className="font-semibold mb-2 text-lg text-cyan-300">{item.title}</h3>
              <p className="text-gray-300 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-wider">
          Upgrade Your Retail Operations with AI
        </h2>
        <p className="text-lg max-w-xl mx-auto mb-6">
                    Ready to transform your e-commerce or retail business? Let’s provide the datasets and AI solutions to drive smarter decisions.
        </p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-cyan-400 text-gray-900 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-cyan-500 transition"
        >
          Request a Demo
        </motion.button>
      </motion.section>
    </div>
  );
}
